import type { AccessSettings, AccessUser, AddAccessUserInput, AddTalentListMemberInput, AiQueueRecord, AiQueueStatus, Application, CompanyDirectoryUser, CreateTalentListInput, DashboardApplication, DashboardJobWorkload, JobConfig, RegionCode, TalentListView, UpdateExtractionDetailsInput, UpdateTalentListInput, UserRole } from "@cv-screener/shared";
import { APPLICATION_STATUSES, redactProtectedCandidateDetails, splitLegacyApplicationTags } from "@cv-screener/shared";
import { demoApplication, demoApplications, demoJobs, demoTalentLists, demoUsage } from "../demo-data";
import { demoAiQueue } from "../demo-ai-queue";
import type { AiQueueSortDirection, AiQueueSortKey } from "./ai-queue-sort";
import type { ProcessingHistoryRecord } from "./report-preview";
import { demoMode } from "./runtime";

const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";
const impersonationStorageKey = "cv-impersonate-user-email";
const rolePreviewStorageKey = "cv-preview-role";
const demoApplicationClassificationsStorageKey = "cv-demo-application-classifications";
const legacyDemoApplicationTagsStorageKey = "cv-demo-application-tags";
const staleImpersonationCodes = new Set(["IMPERSONATION_TARGET_NOT_FOUND", "IMPERSONATION_FORBIDDEN", "ROLE_PREVIEW_FORBIDDEN"]);
let tokenProvider: (() => Promise<string>) | undefined;
type PreviewRole = Exclude<UserRole, "SuperAdmin">;

function applicationForReview(application: Application): Application {
  return { ...application, candidate: redactProtectedCandidateDetails(application.candidate) };
}

export type AiQueueSummary = Omit<AiQueueRecord, "requestSnapshot" | "responseText"> & { requestPartCount: number; responseAvailable: boolean };
export interface AiQueueListResult {
  items: AiQueueSummary[];
  counts: Record<AiQueueStatus, number>;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

function storedPreviewRole(): PreviewRole | undefined {
  const value = localStorage.getItem(rolePreviewStorageKey);
  return value === "Admin" || value === "Recruiter" || value === "Viewer" ? value : undefined;
}

export interface SessionUser {
  objectId: string;
  tenantId: string;
  email: string;
  displayName: string;
  region: RegionCode;
  role: UserRole;
  assignedJobIds: string[];
}

export interface SessionIdentity {
  user: SessionUser;
  actor: SessionUser;
  isImpersonating: boolean;
  isRolePreview: boolean;
  isSuperAdmin: boolean;
  canImpersonate: boolean;
  dependencies?: {
    sharePoint: "available" | "degraded";
    accessSource: "sharepoint" | "bootstrap" | "stale-cache";
  };
}

export interface DashboardDependencies {
  sharePoint: {
    status: "available" | "degraded";
    affected: string[];
  };
  directory?: {
    status: "available" | "degraded";
    affected: string[];
  };
}

export function configureTokenProvider(provider: () => Promise<string>): void {
  tokenProvider = provider;
}

export class ApiError extends Error {
  constructor(message: string, readonly code: string, readonly requestId?: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await tokenProvider?.();
  const impersonatedEmail = localStorage.getItem(impersonationStorageKey)?.trim().toLowerCase();
  const previewRole = storedPreviewRole();
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(impersonatedEmail ? { "x-impersonate-user-email": impersonatedEmail } : {}),
      ...(previewRole ? { "x-preview-role": previewRole } : {}),
      ...init?.headers,
    },
  });
  const payload = await response.json() as T & { error?: { message: string; code: string; requestId?: string } };
  if (!response.ok && payload.error?.code && staleImpersonationCodes.has(payload.error.code) && (impersonatedEmail || previewRole)) {
    localStorage.removeItem(impersonationStorageKey);
    localStorage.removeItem(rolePreviewStorageKey);
    return request<T>(path, init);
  }
  if (!response.ok) throw new ApiError(payload.error?.message ?? "Request failed", payload.error?.code ?? "REQUEST_FAILED", payload.error?.requestId);
  return payload;
}

export function contentDispositionFileName(value: string | null): string | undefined {
  if (!value) return undefined;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(value)?.[1];
  const regular = /filename="([^"]+)"/i.exec(value)?.[1] ?? /filename=([^;]+)/i.exec(value)?.[1];
  const encodedName = extended ?? regular?.trim();
  if (!encodedName) return undefined;
  let decodedName = encodedName;
  try { decodedName = decodeURIComponent(encodedName); } catch { /* Keep the server-provided filename when it is not URI encoded. */ }
  return decodedName.split(/[\\/]/).at(-1)?.trim() || undefined;
}

async function requestFile(path: string): Promise<{ blob: Blob; fileName?: string }> {
  const token = await tokenProvider?.();
  const impersonatedEmail = localStorage.getItem(impersonationStorageKey)?.trim().toLowerCase();
  const previewRole = storedPreviewRole();
  const response = await fetch(`${baseUrl}${path}`, {
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(impersonatedEmail ? { "x-impersonate-user-email": impersonatedEmail } : {}),
      ...(previewRole ? { "x-preview-role": previewRole } : {}),
    },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined) as { error?: { message?: string; code?: string; requestId?: string } } | undefined;
    if (payload?.error?.code && staleImpersonationCodes.has(payload.error.code) && (impersonatedEmail || previewRole)) {
      localStorage.removeItem(impersonationStorageKey);
      localStorage.removeItem(rolePreviewStorageKey);
      return requestFile(path);
    }
    throw new ApiError(payload?.error?.message ?? "Unable to open document", payload?.error?.code ?? "DOCUMENT_OPEN_FAILED", payload?.error?.requestId);
  }
  const fileName = contentDispositionFileName(response.headers.get("content-disposition"));
  return { blob: await response.blob(), ...(fileName ? { fileName } : {}) };
}

function openBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function csvCell(value: unknown): string {
  const text = value == null ? "" : String(value);
  const safeText = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safeText) ? `"${safeText.replaceAll('"', '""')}"` : safeText;
}

function csvDocument(headers: string[], rows: unknown[][]): string {
  return [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

function fileNameFromPath(path: string): string {
  return path.split("/").at(-1) || "report.csv";
}

function downloadCsv(csv: string, path: string): string {
  const fileName = fileNameFromPath(path);
  downloadBlob(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }), fileName);
  return fileName;
}

function applicationReportCsv(applications: DashboardApplication[]): string {
  const headers = ["Application ID", "Job ID", "Candidate Name", "Candidate Email", "Overall Score", "Recommendation", "Confidence", "Status", "Shortlist Rank", "Applied Date", "Latest Role", "AI State"];
  const rows = applications.map((application) => [application.applicationId, application.jobId, application.candidateName, application.candidateEmail, application.overallScore, application.recommendation, application.confidence, application.status, application.shortlistRank, application.appliedAt, application.latestRole, application.aiState]);
  return csvDocument(headers, rows);
}

function talentListsReportCsv(lists: TalentListView[]): string {
  const headers = ["Talent List", "Target Role", "List ID", "Candidate Name", "Candidate Email", "Latest Role", "Source Job ID", "Application ID", "Overall Score", "Recommendation", "Confidence", "Status", "Shortlist Rank", "Specialties", "Levels", "Reviewer Note", "Added At", "Added By", "Added By Email"];
  const rows = lists.flatMap((list) => list.members.map((member) => [list.name, list.targetRole, list.talentListId, member.application.candidateName, member.application.candidateEmail, member.application.latestRole, member.jobId, member.applicationId, member.application.overallScore, member.application.recommendation, member.application.confidence, member.application.status, member.application.shortlistRank, member.specialties.join(" | "), member.levels.join(" | "), member.note, member.addedAt, member.addedBy.displayName, member.addedBy.email]));
  return csvDocument(headers, rows);
}

const delay = <T,>(value: T): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), 180));
const demoTalentListState: TalentListView[] = demoTalentLists.map((list) => ({ ...list, members: list.members.map((member) => ({ ...member, application: { ...member.application } })) }));
type ApplicationClassifications = { specialties: string[]; levels: string[] };
const demoApplicationClassificationsState = loadDemoApplicationClassifications();
const demoExtractionDetailsState = new Map<string, UpdateExtractionDetailsInput>();
const demoActor: SessionUser = { objectId: "u-admin", tenantId: "demo", email: "admin@company.example", displayName: "Demo Administrator", region: "HK", role: "SuperAdmin", assignedJobIds: [] };

function loadDemoApplicationClassifications(): Map<string, ApplicationClassifications> {
  const seeded = new Map<string, ApplicationClassifications>([["APP-A18F09CD22B1", { specialties: ["Retail"], levels: ["Senior"] }]]);
  try {
    const saved = globalThis.localStorage?.getItem(demoApplicationClassificationsStorageKey) ?? globalThis.localStorage?.getItem(legacyDemoApplicationTagsStorageKey);
    if (!saved) return seeded;
    const parsed = JSON.parse(saved) as Record<string, unknown>;
    Object.entries(parsed).forEach(([applicationId, value]) => {
      if (Array.isArray(value) && value.every((tag) => typeof tag === "string")) seeded.set(applicationId, splitLegacyApplicationTags(value));
      else if (value && typeof value === "object") {
        const record = value as Record<string, unknown>;
        if (Array.isArray(record.specialties) && record.specialties.every((tag) => typeof tag === "string") && Array.isArray(record.levels) && record.levels.every((tag) => typeof tag === "string")) seeded.set(applicationId, { specialties: record.specialties, levels: record.levels });
      }
    });
  } catch {
    // Demo storage is optional; retain the seeded examples when browser storage is unavailable.
  }
  return seeded;
}

function saveDemoApplicationClassifications(): void {
  try {
    globalThis.localStorage?.setItem(demoApplicationClassificationsStorageKey, JSON.stringify(Object.fromEntries(demoApplicationClassificationsState)));
  } catch {
    // Demo mode can still operate in-memory when browser storage is unavailable.
  }
}

function demoApplicationClassificationOptions(): ApplicationClassifications {
  const unique = (values: string[]) => [...new Map(values.map((value) => [value.toLocaleLowerCase(), value])).values()].sort((left, right) => left.localeCompare(right));
  return {
    specialties: unique(["Retail", "Commercial", "Building Services", "Project delivery", ...[...demoApplicationClassificationsState.values()].flatMap((value) => value.specialties)]),
    levels: unique(["Junior", "Mid-level", "Senior", "Manager", "Director", ...[...demoApplicationClassificationsState.values()].flatMap((value) => value.levels)]),
  };
}

function demoCurrentUser(): SessionUser {
  const previewRole = storedPreviewRole();
  if (previewRole) return { ...demoActor, role: previewRole, assignedJobIds: demoJobs.map((job) => job.jobId) };
  const targetEmail = localStorage.getItem(impersonationStorageKey)?.trim().toLowerCase();
  const match = demoAccess.users.find((entry) => entry.enabled && entry.email.toLowerCase() === targetEmail);
  if (!match) return demoActor;
  return {
    objectId: match.objectId || `demo:${match.email}`,
    tenantId: match.tenantId || "demo",
    email: match.email,
    displayName: match.displayName,
    region: match.region,
    role: match.role,
    assignedJobIds: match.assignedJobIds,
  };
}

function demoCanAccessJob(jobId: string): boolean {
  const current = demoCurrentUser();
  return current.role === "SuperAdmin" || (current.role === "Admin" ? current.assignedJobIds.length === 0 || current.assignedJobIds.includes(jobId) : current.assignedJobIds.includes(jobId));
}

function visibleDemoJobs(): JobConfig[] {
  return demoJobs.filter((job) => demoCanAccessJob(job.jobId));
}

function visibleDemoApplications(): DashboardApplication[] {
  return demoApplications.filter((application) => demoCanAccessJob(application.jobId));
}

const demoProcessingHistory: ProcessingHistoryRecord[] = (() => {
  const records: ProcessingHistoryRecord[] = [];
  const referenceTime = Date.UTC(2026, 7, 17, 9);
  for (let year = 2022; year <= 2026; year += 1) {
    for (let month = 0; month < 12; month += 1) {
      if (Date.UTC(year, month, 1) > referenceTime) continue;
      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      demoJobs.forEach((job, jobIndex) => {
        const volume = 4 + (year + month * 3 + jobIndex * 5) % 7;
        for (let eventIndex = 0; eventIndex < volume; eventIndex += 1) {
          const day = 1 + (eventIndex * 4 + jobIndex * 3 + month) % daysInMonth;
          const hour = 1 + (eventIndex * 3 + jobIndex) % 16;
          const appliedAt = new Date(Date.UTC(year, month, day, hour));
          if (appliedAt.getTime() > referenceTime) continue;
          const signal = year + month * 11 + jobIndex * 7 + eventIndex * 13;
          const ageDays = Math.floor((referenceTime - appliedAt.getTime()) / 86_400_000);
          const aiState: ProcessingHistoryRecord["aiState"] = ageDays <= 3 && signal % 9 === 0
            ? "Processing"
            : ageDays <= 5 && signal % 11 === 0
              ? "Pending"
              : signal % 19 === 0
                ? "Failed"
                : signal % 23 === 0
                  ? "Outdated"
                  : "Completed";
          const updatedAt = new Date(appliedAt.getTime() + (20 + signal % 220) * 60_000);
          records.push({ jobId: job.jobId, appliedAt: appliedAt.toISOString(), updatedAt: updatedAt.toISOString(), aiState });
        }
      });
    }
  }
  return records;
})();

function demoApplicationRecord(jobId: string, applicationId: string): Application {
  const row = demoApplications.find((item) => item.applicationId === applicationId);
  const application: Application = {
    ...demoApplication,
    applicationId,
    jobId,
    ...(row ? {
      candidate: { ...demoApplication.candidate, name: row.candidateName, email: row.candidateEmail },
      scoring: { ...demoApplication.scoring, overallScore: row.overallScore, recommendation: row.recommendation as Application["scoring"]["recommendation"], confidence: row.confidence as Application["scoring"]["confidence"] },
      audit: { ...demoApplication.audit, aiState: row.aiState },
    } : {}),
    review: {
      ...demoApplication.review,
      ...(row ? { status: row.status, shortlistRank: row.shortlistRank, nextAction: row.nextAction } : {}),
      specialties: [...(demoApplicationClassificationsState.get(applicationId)?.specialties ?? demoApplication.review.specialties)],
      levels: [...(demoApplicationClassificationsState.get(applicationId)?.levels ?? demoApplication.review.levels)],
    },
  };
  const corrected = demoExtractionDetailsState.get(applicationId);
  return corrected ? {
    ...application,
    candidate: structuredClone(corrected.candidate),
    extractedCvProfile: structuredClone(corrected.extractedCvProfile),
    audit: { ...application.audit, aiState: "Outdated", lastUpdatedAt: new Date().toISOString() },
  } : application;
}

function copyTalentLists(): TalentListView[] {
  return demoTalentListState.map((list) => ({ ...list, members: list.members.map((member) => ({ ...member, application: { ...member.application } })) }));
}

type DemoPerformanceAction = "process-incoming-cv" | "rescore-candidate" | "reextract-cv-profile" | "bulk-rescore";

type DemoPerformanceError = {
  action: DemoPerformanceAction;
  timestamp: string;
  errorCode: string;
  errorReason: string;
  sharePointPath: string;
  user: string;
  jobId: string;
  applicationId: string;
};

const demoPerformanceAttempts = (() => {
  const actions: DemoPerformanceAction[] = ["process-incoming-cv", "rescore-candidate", "reextract-cv-profile", "bulk-rescore"];
  const errorDetails = [
    ["AI_PROVIDER_TIMEOUT", "Gemini did not return a response within the 30-second worker timeout."],
    ["INVALID_AI_RESPONSE", "The AI response was missing the required five scoring categories."],
    ["AI_PROVIDER_RATE_LIMIT", "The AI provider rate limit was reached during screening."],
    ["CV_TEXT_EXTRACTION_FAILED", "The source CV did not contain readable text."],
  ] as const;
  return Array.from({ length: 30 }, (_, dayIndex) => {
    const date = new Date(Date.UTC(2026, 6, 19 + dayIndex));
    const count = 8 + dayIndex % 4;
    return Array.from({ length: count }, (_, attemptIndex) => {
      const startedAt = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 1 + attemptIndex, (dayIndex * 7 + attemptIndex * 11) % 60));
      const durationMs = 18_000 + (dayIndex * 1_700 + attemptIndex * 2_300) % 15_000;
      const forcedCurrentFailure = dayIndex === 29 && attemptIndex >= count - 3;
      const failed = forcedCurrentFailure || (dayIndex * 13 + attemptIndex * 17) % 43 === 0;
      const action = actions[(dayIndex + attemptIndex) % actions.length]!;
      if (!failed) return { startedAt: startedAt.toISOString(), durationMs, error: undefined };
      const detail = errorDetails[(dayIndex + attemptIndex) % errorDetails.length]!;
      const job = demoJobs[(dayIndex + attemptIndex) % demoJobs.length]!;
      const applicationId = `APP-DEMO-${String(dayIndex * 20 + attemptIndex + 1).padStart(4, "0")}`;
      const timestamp = new Date(startedAt.getTime() + durationMs).toISOString();
      const error: DemoPerformanceError = {
        action,
        timestamp,
        errorCode: detail[0],
        errorReason: detail[1],
        sharePointPath: `${job.jobId}/processed/${applicationId.toLowerCase()}`,
        user: action === "process-incoming-cv" ? "scheduled-worker" : "admin@company.example",
        jobId: job.jobId,
        applicationId,
      };
      return { startedAt: startedAt.toISOString(), durationMs, error };
    });
  }).flat();
})();

export const api = {
  isDemo: demoMode,

  async me() {
    if (demoMode) {
      const current = demoCurrentUser();
      return delay({ user: current, actor: demoActor, isImpersonating: current.email !== demoActor.email || Boolean(storedPreviewRole()), isRolePreview: Boolean(storedPreviewRole()), isSuperAdmin: true, canImpersonate: true, dependencies: { sharePoint: "available", accessSource: "bootstrap" } } satisfies SessionIdentity);
    }
    try {
      return await request<SessionIdentity>("/api/me");
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== "IMPERSONATION_TARGET_NOT_FOUND") throw error;
      localStorage.removeItem(impersonationStorageKey);
      return request<SessionIdentity>("/api/me");
    }
  },

  async impersonationUsers() {
    if (demoMode) {
      return delay({ users: demoAccess.users.filter((entry) => entry.enabled) });
    }
    return request<{ users: AccessUser[] }>("/api/impersonation/users");
  },

  async setImpersonation(targetEmail?: string) {
    const normalizedTarget = targetEmail?.trim().toLowerCase();
    const currentTarget = localStorage.getItem(impersonationStorageKey)?.trim().toLowerCase();
    const currentRole = storedPreviewRole();
    if (normalizedTarget) {
      if (!demoMode) await request("/api/impersonation/audit", { method: "POST", body: JSON.stringify({ targetEmail: normalizedTarget, active: true }) });
      localStorage.setItem(impersonationStorageKey, normalizedTarget);
      localStorage.removeItem(rolePreviewStorageKey);
      return;
    }
    if (!currentTarget && !currentRole) return;
    try {
      if (!demoMode) await request("/api/impersonation/audit", {
        method: "POST",
        body: JSON.stringify(currentTarget ? { targetEmail: currentTarget, active: false } : { targetRole: currentRole, active: false }),
      });
    } finally {
      localStorage.removeItem(impersonationStorageKey);
      localStorage.removeItem(rolePreviewStorageKey);
    }
  },

  async setRolePreview(role: PreviewRole) {
    const currentRole = storedPreviewRole();
    if (currentRole === role) return;
    if (!demoMode) await request("/api/impersonation/audit", { method: "POST", body: JSON.stringify({ targetRole: role, active: true }) });
    localStorage.setItem(rolePreviewStorageKey, role);
    localStorage.removeItem(impersonationStorageKey);
  },

  async dashboard() {
    if (demoMode) {
      const jobs = visibleDemoJobs();
      const applications = visibleDemoApplications();
      return delay({
      jobs,
      applications,
      processingHistory: demoProcessingHistory.filter((record) => demoCanAccessJob(record.jobId)),
      usage: demoUsage,
      workloads: [
        { jobId: "QS-2026-01", receivedApplications: 5, processedApplications: 3, processingApplications: 1, pendingApplications: 0, errorApplications: 1 },
        { jobId: "PM-2026-03", receivedApplications: 4, processedApplications: 3, processingApplications: 0, pendingApplications: 1, errorApplications: 0 },
        { jobId: "DD-2026-02", receivedApplications: 0, processedApplications: 0, processingApplications: 0, pendingApplications: 0, errorApplications: 0 },
        { jobId: "SEC-2026-04", receivedApplications: 12, processedApplications: 9, processingApplications: 1, pendingApplications: 2, errorApplications: 0 },
        { jobId: "ACC-2026-05", receivedApplications: 7, processedApplications: 5, processingApplications: 0, pendingApplications: 1, errorApplications: 1 },
        { jobId: "PC-2026-06", receivedApplications: 18, processedApplications: 15, processingApplications: 1, pendingApplications: 2, errorApplications: 0 },
        { jobId: "BS-2026-07", receivedApplications: 6, processedApplications: 4, processingApplications: 0, pendingApplications: 2, errorApplications: 0 },
        { jobId: "ID-2026-08", receivedApplications: 10, processedApplications: 8, processingApplications: 1, pendingApplications: 0, errorApplications: 1 },
        { jobId: "AQS-2026-09", receivedApplications: 21, processedApplications: 17, processingApplications: 1, pendingApplications: 3, errorApplications: 0 },
      ].filter((workload) => demoCanAccessJob(workload.jobId)) satisfies DashboardJobWorkload[],
      metrics: {
        openJobs: jobs.filter((job) => job.status === "Open").length,
        totalApplications: applications.length,
        averageScore: applications.length ? Math.round(applications.reduce((sum, app) => sum + app.overallScore, 0) / applications.length * 10) / 10 : 0,
        statusCounts: Object.fromEntries(APPLICATION_STATUSES.map((status) => [status, applications.filter((app) => app.status === status).length])),
      },
      dependencies: { sharePoint: { status: "available" as const, affected: [] } },
    });
    }
    const dashboard = await request<{ jobs: JobConfig[]; applications: DashboardApplication[]; workloads: DashboardJobWorkload[]; usage: typeof demoUsage; metrics: { openJobs: number; totalApplications: number; averageScore: number; statusCounts: Record<string, number> }; dependencies: DashboardDependencies }>("/api/dashboard");
    return {
      ...dashboard,
      processingHistory: dashboard.applications.map(({ jobId, appliedAt, updatedAt, aiState }) => ({ jobId, appliedAt, updatedAt, aiState })),
    };
  },

  async jobs() {
    if (demoMode) return delay({ jobs: visibleDemoJobs(), dependencies: { sharePoint: { status: "available" as const, affected: [] } } });
    return request<{ jobs: JobConfig[]; dependencies: DashboardDependencies }>("/api/jobs");
  },

  async job(jobId: string) {
    if (demoMode) {
      const config = demoJobs.find((job) => job.jobId === jobId && demoCanAccessJob(job.jobId));
      if (!config) throw new ApiError("You do not have access to this job", "JOB_FORBIDDEN");
      return delay({ config, markdown: sampleJobMarkdown(config) });
    }
    return request<{ config: JobConfig; markdown: string }>(`/api/jobs/${jobId}`);
  },

  async saveJob(payload: Record<string, unknown>, jobId?: string) {
    if (demoMode) return delay({ config: { ...demoJobs[0], ...payload }, markdown: String(payload.markdown ?? "") });
    return request(jobId ? `/api/jobs/${jobId}` : "/api/jobs", { method: jobId ? "PUT" : "POST", body: JSON.stringify(payload) });
  },

  async updateJobReviewTeam(jobId: string, payload: { leadHiringManagerId: string; hiringManagerIds: string[]; reviewTeamIds: string[] }) {
    if (demoMode) {
      const job = demoJobs.find((item) => item.jobId === jobId) ?? demoJobs[0]!;
      Object.assign(job, payload, { updatedAt: new Date().toISOString() });
      return delay({ config: { ...job }, markdown: sampleJobMarkdown(job) });
    }
    return request<{ config: JobConfig; markdown: string }>(`/api/jobs/${jobId}/review-team`, { method: "PATCH", body: JSON.stringify(payload) });
  },

  async closeJob(jobId: string) {
    if (demoMode) {
      const job = demoJobs.find((item) => item.jobId === jobId) ?? demoJobs[0]!;
      const closedAt = new Date().toISOString();
      job.status = "Closed";
      job.closedAt = closedAt;
      return delay({ config: { ...job }, markdown: sampleJobMarkdown(job) });
    }
    return request<{ config: JobConfig; markdown: string }>(`/api/jobs/${jobId}`, { method: "PUT", body: JSON.stringify({ status: "Closed" }) });
  },

  async generateMarkdown(payload: { jobId: string; jobTitle: string; department: string; location: string; description: string }) {
    if (demoMode) return delay({ markdown: sampleJobMarkdown({ ...demoJobs[0]!, ...payload }), validation: { valid: true, weights: [30, 30, 20, 10, 10], total: 100, errors: [] } });
    return request<{ markdown: string; validation: { valid: boolean; total: number; errors: string[] } }>("/api/jobs/generate-markdown", { method: "POST", body: JSON.stringify(payload) });
  },

  async applications() {
    if (demoMode) return delay({ applications: visibleDemoApplications(), dependencies: { sharePoint: { status: "available" as const, affected: [] } } });
    return request<{ applications: DashboardApplication[]; dependencies: DashboardDependencies }>("/api/applications");
  },

  async talentLists() {
    if (demoMode) return delay({ talentLists: copyTalentLists().map((list) => ({ ...list, members: list.members.filter((member) => demoCanAccessJob(member.jobId)) })), dependencies: { sharePoint: { status: "available" as const, affected: [] } } });
    return request<{ talentLists: TalentListView[]; dependencies: DashboardDependencies }>("/api/talent-lists");
  },

  async createTalentList(payload: CreateTalentListInput) {
    if (demoMode) {
      const now = new Date().toISOString();
      const talentList: TalentListView = {
        schemaVersion: "1.0",
        region: demoCurrentUser().region,
        talentListId: crypto.randomUUID(),
        ...payload,
        description: "",
        createdBy: demoActor,
        createdAt: now,
        updatedBy: demoActor,
        updatedAt: now,
        members: [],
      };
      demoTalentListState.unshift(talentList);
      return delay({ talentList: { ...talentList, members: [] } });
    }
    return request<{ talentList: TalentListView }>("/api/talent-lists", { method: "POST", body: JSON.stringify(payload) });
  },

  async updateTalentList(talentListId: string, payload: UpdateTalentListInput) {
    if (demoMode) {
      const list = demoTalentListState.find((item) => item.talentListId === talentListId);
      if (!list) throw new ApiError("Talent list was not found", "TALENT_LIST_NOT_FOUND");
      Object.assign(list, payload, { updatedAt: new Date().toISOString(), updatedBy: demoActor });
      return delay({ talentList: copyTalentLists().find((item) => item.talentListId === talentListId)! });
    }
    return request<{ talentList: TalentListView }>(`/api/talent-lists/${encodeURIComponent(talentListId)}`, { method: "PATCH", body: JSON.stringify(payload) });
  },

  async deleteTalentList(talentListId: string) {
    if (demoMode) {
      const index = demoTalentListState.findIndex((item) => item.talentListId === talentListId);
      if (index < 0) throw new ApiError("Talent list was not found", "TALENT_LIST_NOT_FOUND");
      demoTalentListState.splice(index, 1);
      return delay({ deleted: true as const, talentListId });
    }
    return request<{ deleted: true; talentListId: string }>(`/api/talent-lists/${encodeURIComponent(talentListId)}`, { method: "DELETE" });
  },

  async addTalentListMember(talentListId: string, payload: AddTalentListMemberInput) {
    if (payload.levels.length > 1) throw new ApiError("Select one level only", "LEVEL_SELECTION_LIMIT");
    if (demoMode) {
      const list = demoTalentListState.find((item) => item.talentListId === talentListId);
      const application = demoApplications.find((item) => item.jobId === payload.jobId && item.applicationId === payload.applicationId);
      if (!list || !application) throw new ApiError("Talent list or application was not found", "NOT_FOUND");
      const existing = list.members.find((member) => member.jobId === payload.jobId && member.applicationId === payload.applicationId);
      if (!existing) {
        const now = new Date().toISOString();
        list.members.push({ membershipId: crypto.randomUUID(), ...payload, addedBy: demoActor, addedAt: now, application: { ...application } });
        list.updatedAt = now;
        list.updatedBy = demoActor;
      }
      return delay({ talentList: copyTalentLists().find((item) => item.talentListId === talentListId)! });
    }
    return request<{ talentList: TalentListView }>(`/api/talent-lists/${encodeURIComponent(talentListId)}/members`, { method: "POST", body: JSON.stringify(payload) });
  },

  async removeTalentListMember(talentListId: string, membershipId: string) {
    if (demoMode) {
      const list = demoTalentListState.find((item) => item.talentListId === talentListId);
      if (!list) throw new ApiError("Talent list was not found", "TALENT_LIST_NOT_FOUND");
      const index = list.members.findIndex((member) => member.membershipId === membershipId);
      if (index >= 0) list.members.splice(index, 1);
      list.updatedAt = new Date().toISOString();
      list.updatedBy = demoActor;
      return delay({ talentList: copyTalentLists().find((item) => item.talentListId === talentListId)! });
    }
    return request<{ talentList: TalentListView }>(`/api/talent-lists/${encodeURIComponent(talentListId)}/members/${encodeURIComponent(membershipId)}`, { method: "DELETE" });
  },

  async application(jobId: string, applicationId: string) {
    if (demoMode) {
      if (!demoCanAccessJob(jobId)) throw new ApiError("You do not have access to this job", "JOB_FORBIDDEN");
      return delay({ application: applicationForReview(demoApplicationRecord(jobId, applicationId)) });
    }
    return request<{ application: Application }>(`/api/jobs/${jobId}/applications/${applicationId}`);
  },

  async openDocument(jobId: string, applicationId: string, kind: "cv" | "cover-letter" | "application-json") {
    if (demoMode) {
      if (kind !== "application-json") return false;
      if (demoCurrentUser().role !== "SuperAdmin") throw new ApiError("Only a Super Admin can open application JSON", "APPLICATION_JSON_FORBIDDEN");
      openBlob(new Blob([JSON.stringify(demoApplicationRecord(jobId, applicationId), null, 2)], { type: "application/json" }));
      return true;
    }
    const file = await requestFile(`/api/jobs/${jobId}/applications/${applicationId}/files/${kind}`);
    openBlob(file.blob);
    return true;
  },

  async downloadDocument(jobId: string, applicationId: string, kind: "cv" | "cover-letter") {
    if (demoMode) return false;
    const file = await requestFile(`/api/jobs/${jobId}/applications/${applicationId}/files/${kind}`);
    const fallbackExtension = file.blob.type === "application/pdf" ? "pdf" : "docx";
    const fileName = file.fileName ?? `${applicationId}-${kind}.${fallbackExtension}`;
    downloadBlob(file.blob, fileName);
    return fileName;
  },

  async updateReview(jobId: string, applicationId: string, payload: { status: string; note: string; shortlistRank?: number | null; overallScore?: number; specialties?: string[]; levels?: string[] }) {
    if (payload.levels && payload.levels.length > 1) throw new ApiError("Select one level only", "LEVEL_SELECTION_LIMIT");
    if (demoMode) {
      const currentUser = demoCurrentUser();
      const currentApplication = demoApplicationRecord(jobId, applicationId);
      if (currentUser.role === "Viewer") {
        if (!payload.specialties || !payload.levels || payload.status !== currentApplication.review.status || payload.shortlistRank !== undefined || payload.note || payload.overallScore !== undefined) throw new ApiError("Reviewers can select existing application specialties and levels only", "VIEWER_CLASSIFICATIONS_ONLY");
        const options = demoApplicationClassificationOptions();
        const allowedSpecialties = new Set([...options.specialties, ...currentApplication.review.specialties].map((tag) => tag.toLocaleLowerCase()));
        const allowedLevels = new Set([...options.levels, ...currentApplication.review.levels].map((tag) => tag.toLocaleLowerCase()));
        if (payload.specialties.some((tag) => !allowedSpecialties.has(tag.toLocaleLowerCase())) || payload.levels.some((tag) => !allowedLevels.has(tag.toLocaleLowerCase()))) throw new ApiError("Reviewers cannot create new specialties or levels", "CLASSIFICATION_CREATION_FORBIDDEN");
      }
      const shortlistRank = payload.status === "Shortlisted" ? payload.shortlistRank === undefined ? currentApplication.review.shortlistRank : payload.shortlistRank : null;
      if (shortlistRank !== null && demoApplications.some((application) => application.jobId === jobId && application.applicationId !== applicationId && application.status === "Shortlisted" && application.shortlistRank === shortlistRank)) {
        throw new ApiError(`Shortlist rank #${shortlistRank} is already assigned for this job`, "SHORTLIST_RANK_TAKEN");
      }
      const row = demoApplications.find((item) => item.applicationId === applicationId);
      if (row) {
        row.status = payload.status as typeof row.status;
        row.shortlistRank = shortlistRank;
        if (payload.overallScore !== undefined) row.overallScore = payload.overallScore;
        row.updatedAt = new Date().toISOString();
      }
      if (payload.specialties && payload.levels) {
        demoApplicationClassificationsState.set(applicationId, { specialties: [...payload.specialties], levels: [...payload.levels] });
        saveDemoApplicationClassifications();
      }
      const current = demoApplicationRecord(jobId, applicationId);
      const specialties = payload.specialties ?? current.review.specialties;
      const levels = payload.levels ?? current.review.levels;
      return delay({ application: applicationForReview({ ...current, scoring: { ...current.scoring, ...(payload.overallScore === undefined ? {} : { overallScore: payload.overallScore }) }, review: { ...current.review, status: payload.status as Application["review"]["status"], shortlistRank, specialties, levels, latestNote: payload.note || current.review.latestNote } }) });
    }
    return request<{ application: Application }>(`/api/jobs/${jobId}/applications/${applicationId}/review`, { method: "PATCH", body: JSON.stringify(payload) });
  },

  async updateExtractionDetails(jobId: string, applicationId: string, payload: UpdateExtractionDetailsInput) {
    if (demoMode) {
      if (!demoCanAccessJob(jobId)) throw new ApiError("You do not have access to this job", "JOB_FORBIDDEN");
      demoExtractionDetailsState.set(applicationId, structuredClone(payload));
      return delay({ application: applicationForReview(demoApplicationRecord(jobId, applicationId)) });
    }
    return request<{ application: Application }>(`/api/jobs/${jobId}/applications/${applicationId}/extraction`, { method: "PATCH", body: JSON.stringify(payload) });
  },

  async rescore(jobId: string, applicationId: string, profileOnly = false) {
    if (demoMode) return delay({ application: applicationForReview(demoApplicationRecord(jobId, applicationId)) });
    const action = profileOnly ? "reextract" : "rescore";
    return request<{ application: Application }>(`/api/jobs/${jobId}/applications/${applicationId}/${action}`, { method: "POST" });
  },

  async bulkRescore(groups: { jobId: string; applicationIds: string[] }[]) {
    if (demoMode) return delay({ operations: groups.map((group) => ({ operationId: `BULK-${group.jobId}`, status: "Queued", count: group.applicationIds.length })) });
    const operations = await Promise.all(groups.map((group) => request<{ operation: { operationId: string; status: string } }>(`/api/jobs/${group.jobId}/bulk-rescore`, { method: "POST", body: JSON.stringify({ applicationIds: group.applicationIds }) })));
    return { operations: operations.map((result) => result.operation) };
  },

  async aiSettings() {
    if (demoMode) return delay({ settings: { schemaVersion: "1.0", region: "HK", dailyLimit: 1000, resetTimezone: "America/Los_Angeles", updatedBy: "admin@company.example", updatedAt: "2026-08-11T06:00:00.000Z" }, model: "gemini-3.5-flash-lite", usage: demoUsage });
    const [settings, usage] = await Promise.all([request<{ settings: { region: RegionCode; dailyLimit: number; resetTimezone: string; updatedBy: string; updatedAt: string }; model: string }>("/api/ai-settings"), request<{ usage: typeof demoUsage }>("/api/ai-usage")]);
    return { ...settings, usage: usage.usage };
  },

  async aiPerformance({ startDate, endDate, errorPage = 1, errorPageSize = 2 }: { startDate: string; endDate: string; errorPage?: number; errorPageSize?: number }) {
    const rangeStart = Date.parse(`${startDate}T00:00:00.000Z`);
    const rangeEndExclusive = Date.parse(`${endDate}T00:00:00.000Z`) + 86_400_000;
    const attempts = demoPerformanceAttempts.filter((attempt) => {
      const timestamp = Date.parse(attempt.startedAt);
      return timestamp >= rangeStart && timestamp < rangeEndExclusive;
    });
    const failures = attempts.flatMap((attempt) => attempt.error ? [attempt.error] : []).sort((left, right) => Date.parse(right.timestamp) - Date.parse(left.timestamp));
    const recentErrorCount = failures.length;
    const errorPageCount = Math.max(1, Math.ceil(recentErrorCount / errorPageSize));
    const resolvedErrorPage = Math.min(errorPage, errorPageCount);
    const errorStart = (resolvedErrorPage - 1) * errorPageSize;
    const failedScreenings = failures.length;
    const tokenPoints = new Map<string, { date: string; inputTokens: number; outputTokens: number; totalTokens: number; requests: number }>();
    for (const record of demoAiQueue) {
      const consumedAt = Date.parse(record.completedAt ?? record.updatedAt);
      if (!Number.isFinite(consumedAt) || consumedAt < rangeStart || consumedAt >= rangeEndExclusive) continue;
      if (record.inputTokens == null && record.outputTokens == null && record.totalTokens == null) continue;
      const date = new Date(consumedAt).toISOString().slice(0, 10);
      const point = tokenPoints.get(date) ?? { date, inputTokens: 0, outputTokens: 0, totalTokens: 0, requests: 0 };
      point.inputTokens += record.inputTokens ?? 0;
      point.outputTokens += record.outputTokens ?? 0;
      point.totalTokens += record.totalTokens ?? (record.inputTokens ?? 0) + (record.outputTokens ?? 0);
      point.requests += 1;
      tokenPoints.set(date, point);
    }
    const points = [...tokenPoints.values()].sort((left, right) => left.date.localeCompare(right.date));
    const tokenTotals = points.reduce((total, point) => ({
      inputTokens: total.inputTokens + point.inputTokens,
      outputTokens: total.outputTokens + point.outputTokens,
      totalTokens: total.totalTokens + point.totalTokens,
      requests: total.requests + point.requests,
    }), { inputTokens: 0, outputTokens: 0, totalTokens: 0, requests: 0 });
    const demoPerformance = {
      startDate,
      endDate,
      windowDays: Math.round((rangeEndExclusive - rangeStart) / 86_400_000),
      totalScreenings: attempts.length,
      completedScreenings: attempts.length - failedScreenings,
      failedScreenings,
      errorRate: attempts.length ? Math.round(failedScreenings / attempts.length * 1000) / 10 : 0,
      averageScreeningTimeMs: attempts.length ? Math.round(attempts.reduce((total, attempt) => total + attempt.durationMs, 0) / attempts.length) : null,
      tokenConsumption: { ...tokenTotals, points },
      recentErrors: failures.slice(errorStart, errorStart + errorPageSize),
      recentErrorCount,
      errorPage: resolvedErrorPage,
      errorPageSize,
    };
    if (demoMode) return delay({ performance: demoPerformance });
    const query = new URLSearchParams({ startDate, endDate, errorPage: String(errorPage), errorPageSize: String(errorPageSize) });
    return request<{ performance: typeof demoPerformance }>(`/api/ai-performance?${query.toString()}`);
  },

  async aiQueue({ status = "all", search = "", fromDate = "", toDate = "", sortBy, sortDirection = "ascending", page = 1, pageSize = 10 }: { status?: AiQueueStatus | "all"; search?: string; fromDate?: string; toDate?: string; sortBy?: AiQueueSortKey; sortDirection?: AiQueueSortDirection; page?: number; pageSize?: number } = {}) {
    if (demoMode) {
      const query = search.trim().toLocaleLowerCase();
      const rangeStart = fromDate ? Date.parse(`${fromDate}T00:00:00.000Z`) : null;
      const rangeEndExclusive = toDate ? Date.parse(`${toDate}T00:00:00.000Z`) + 86_400_000 : null;
      const visibleQueue = demoAiQueue.filter((record) => demoCanAccessJob(record.jobId));
      const counts = Object.fromEntries((["Pending", "Processing", "Completed", "Failed"] as AiQueueStatus[]).map((queueStatus) => [queueStatus, visibleQueue.filter((record) => record.status === queueStatus).length])) as Record<AiQueueStatus, number>;
      const statusRank = (queueStatus: AiQueueStatus) => queueStatus === "Processing" ? 0 : queueStatus === "Pending" ? 1 : 2;
      const compareText = (left: string, right: string) => left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
      const filtered = visibleQueue.filter((record) => {
        const submittedAt = Date.parse(record.sourceSubmittedAt ?? record.createdAt);
        return (status === "all" || record.status === status)
          && (rangeStart == null || submittedAt >= rangeStart)
          && (rangeEndExclusive == null || submittedAt < rangeEndExclusive)
          && (!query || JSON.stringify(record).toLocaleLowerCase().includes(query));
      }).sort((left, right) => {
        if (sortBy) {
          const multiplier = sortDirection === "ascending" ? 1 : -1;
          let difference = 0;
          if (sortBy === "job") difference = compareText(`${left.jobId || "NO-JOB"}\u0000${left.applicationId}\u0000${left.action}`, `${right.jobId || "NO-JOB"}\u0000${right.applicationId}\u0000${right.action}`);
          else if (sortBy === "status") difference = compareText(left.status, right.status);
          else if (sortBy === "submitted") difference = Date.parse(left.sourceSubmittedAt ?? left.createdAt) - Date.parse(right.sourceSubmittedAt ?? right.createdAt);
          else {
            if (left.completedAt == null && right.completedAt != null) return 1;
            if (left.completedAt != null && right.completedAt == null) return -1;
            difference = left.completedAt == null || right.completedAt == null ? 0 : Date.parse(left.completedAt) - Date.parse(right.completedAt);
          }
          if (difference) return difference * multiplier;
        }
        const rankDifference = statusRank(left.status) - statusRank(right.status);
        if (rankDifference) return rankDifference;
        if (statusRank(left.status) < 2) {
          return (left.sourceSubmittedAt ?? left.createdAt).localeCompare(right.sourceSubmittedAt ?? right.createdAt)
            || left.createdAt.localeCompare(right.createdAt);
        }
        return right.createdAt.localeCompare(left.createdAt);
      });
      const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
      const resolvedPage = Math.min(page, pageCount);
      const items = filtered.slice((resolvedPage - 1) * pageSize, resolvedPage * pageSize).map(({ requestSnapshot, responseText, ...record }) => ({ ...record, requestPartCount: requestSnapshot.parts.length, responseAvailable: Boolean(responseText) }));
      return delay({ queue: { items, counts, total: filtered.length, page: resolvedPage, pageSize, pageCount } satisfies AiQueueListResult });
    }
    const query = new URLSearchParams({ status, search, page: String(page), pageSize: String(pageSize) });
    if (fromDate) query.set("fromDate", fromDate);
    if (toDate) query.set("toDate", toDate);
    if (sortBy) {
      query.set("sortBy", sortBy);
      query.set("sortDirection", sortDirection);
    }
    return request<{ queue: AiQueueListResult }>(`/api/ai-queue?${query.toString()}`);
  },

  async aiQueueItem(requestId: string) {
    if (demoMode) {
      const item = demoAiQueue.find((record) => record.requestId === requestId && demoCanAccessJob(record.jobId));
      if (!item) throw new ApiError("AI queue item was not found", "AI_QUEUE_ITEM_NOT_FOUND");
      return delay({ item: structuredClone(item) });
    }
    return request<{ item: AiQueueRecord }>(`/api/ai-queue/${encodeURIComponent(requestId)}`);
  },

  async updateAiLimit(dailyLimit: number) {
    if (demoMode) return delay({ settings: { region: "HK" as const, dailyLimit } });
    return request("/api/ai-settings", { method: "PUT", body: JSON.stringify({ dailyLimit }) });
  },

  async access() {
    if (demoMode) return delay({ access: syncDemoAccess(), dependencies: { sharePoint: { status: "available" as const, affected: [] }, directory: { status: "available" as const, affected: [] } } });
    return request<{ access: AccessSettings; dependencies: DashboardDependencies }>("/api/access");
  },

  async syncAccess() {
    if (demoMode) return delay({ access: syncDemoAccess(), dependencies: { sharePoint: { status: "available" as const, affected: [] }, directory: { status: "available" as const, affected: [] } } });
    return request<{ access: AccessSettings; dependencies: DashboardDependencies }>("/api/access/sync", { method: "POST" });
  },

  async companyDirectoryUsers(search = "") {
    if (demoMode) {
      const needle = search.trim().toLowerCase();
      return delay({ users: demoCompanyDirectoryUsers.filter((entry) => !needle || `${entry.displayName} ${entry.email} ${entry.jobTitle ?? ""} ${entry.department ?? ""}`.toLowerCase().includes(needle)) });
    }
    const query = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
    return request<{ users: CompanyDirectoryUser[] }>(`/api/directory/users${query}`);
  },

  async addAccessUser(input: AddAccessUserInput) {
    if (demoMode) {
      const directoryUser = demoCompanyDirectoryUsers.find((entry) => entry.objectId === input.objectId);
      if (!directoryUser) throw new ApiError("The selected company user no longer exists", "DIRECTORY_USER_NOT_FOUND");
      if (demoAccess.users.some((entry) => entry.objectId === directoryUser.objectId || entry.email === directoryUser.email)) throw new ApiError("This company user already has application access", "ACCESS_USER_EXISTS");
      const timestamp = new Date().toISOString();
      demoAccess.users.push({ ...directoryUser, role: input.role, enabled: true, assignedJobIds: [...new Set(input.assignedJobIds)], createdAt: timestamp, updatedAt: timestamp });
      return delay({ access: structuredClone(demoAccess) });
    }
    return request<{ access: AccessSettings }>("/api/access/users", { method: "POST", body: JSON.stringify(input) });
  },

  async reviewers() {
    if (demoMode) return delay({ users: demoAccess.users.filter((entry) => entry.enabled) });
    return request<{ users: AccessSettings["users"] }>("/api/reviewers");
  },

  async applicationClassifications() {
    if (demoMode) return delay(demoApplicationClassificationOptions());
    return request<ApplicationClassifications>("/api/application-classifications");
  },

  async saveAccess(access: AccessSettings) {
    if (demoMode) return delay({ access });
    return request<{ access: AccessSettings }>("/api/access", { method: "PUT", body: JSON.stringify(access) });
  },

  async exportReport(filters: { jobIds: string[]; statuses: string[]; scoreRanges: Array<{ min: number; max: number }> }) {
    const matches = visibleDemoApplications().filter((application) => {
      const jobMatch = filters.jobIds.length === 0 || filters.jobIds.includes(application.jobId);
      const statusMatch = filters.statuses.length === 0 || filters.statuses.includes(application.status);
      const scoreMatch = filters.scoreRanges.length === 0 || filters.scoreRanges.some((range) => application.overallScore >= range.min && application.overallScore < range.max);
      return jobMatch && statusMatch && scoreMatch;
    });
    if (demoMode) {
      const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
      const path = `_system/reports/applications/HK/applications-${stamp}.csv`;
      const fileName = downloadCsv(applicationReportCsv(matches), path);
      return delay({ path, rows: matches.length, fileName });
    }
    const report = await request<{ path: string; rows: number; csv: string }>("/api/reports/export", { method: "POST", body: JSON.stringify(filters) });
    return { path: report.path, rows: report.rows, fileName: downloadCsv(report.csv, report.path) };
  },

  async exportTalentListsReport(filters: { talentListIds: string[] }) {
    const lists = copyTalentLists()
      .map((list) => ({ ...list, members: list.members.filter((member) => demoCanAccessJob(member.jobId)) }))
      .filter((list) => filters.talentListIds.length === 0 || filters.talentListIds.includes(list.talentListId));
    if (demoMode) {
      const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
      const path = `_system/reports/talent-lists/HK/talent-lists-${stamp}.csv`;
      const fileName = downloadCsv(talentListsReportCsv(lists), path);
      return delay({ path, rows: lists.reduce((total, list) => total + list.members.length, 0), fileName });
    }
    const report = await request<{ path: string; rows: number; csv: string }>("/api/reports/talent-lists/export", { method: "POST", body: JSON.stringify(filters) });
    return { path: report.path, rows: report.rows, fileName: downloadCsv(report.csv, report.path) };
  },
};

const now = "2026-08-11T06:00:00.000Z";
const demoCompanyDirectoryUsers: CompanyDirectoryUser[] = [
  { objectId: "u-admin", tenantId: "demo", email: "admin@company.example", displayName: "Demo Administrator", region: "HK", jobTitle: "Super Admin", department: "Technology" },
  { objectId: "u-melissa", tenantId: "demo", email: "melissa@company.example", displayName: "Melissa Wong", region: "HK", jobTitle: "Recruitment Manager", department: "People and Culture" },
  { objectId: "u-andy", tenantId: "demo", email: "andy@company.example", displayName: "Andy Lam", region: "HK", jobTitle: "Project Director", department: "Projects" },
  { objectId: "u-carol", tenantId: "demo", email: "carol@company.example", displayName: "Carol Cheung", region: "HK", jobTitle: "Talent Partner", department: "People and Culture" },
  { objectId: "u-david", tenantId: "demo", email: "david@company.example", displayName: "David Ho", region: "HK", jobTitle: "Building Services Manager", department: "Projects" },
  { objectId: "u-grace", tenantId: "demo", email: "grace@company.example", displayName: "Grace Lee", region: "HK", jobTitle: "Design Director", department: "Design" },
  { objectId: "u-elaine", tenantId: "demo", email: "elaine@company.example", displayName: "Elaine Chan", region: "HK", jobTitle: "Senior HR Officer", department: "People and Culture" },
  { objectId: "u-kevin", tenantId: "demo", email: "kevin@company.example", displayName: "Kevin Leung", region: "HK", jobTitle: "Commercial Manager", department: "Commercial" },
  { objectId: "u-rachel", tenantId: "demo", email: "rachel@company.example", displayName: "Rachel Ng", region: "HK", jobTitle: "Project Coordinator", department: "Projects" },
];
const demoAccess: AccessSettings = {
  schemaVersion: "1.0",
  users: [
    { email: "admin@company.example", displayName: "Demo Administrator", region: "HK", role: "SuperAdmin", enabled: true, objectId: "u-admin", tenantId: "demo", assignedJobIds: [], createdAt: now, updatedAt: now },
    { email: "elaine@company.example", displayName: "Elaine Chan", region: "HK", role: "Admin", enabled: true, objectId: "u-elaine", tenantId: "demo", assignedJobIds: ["QS-2026-01", "PM-2026-03"], createdAt: now, updatedAt: now },
    { email: "melissa@company.example", displayName: "Melissa Wong", region: "HK", role: "Recruiter", enabled: true, objectId: "u-melissa", tenantId: "demo", assignedJobIds: ["QS-2026-01", "PM-2026-03"], createdAt: now, updatedAt: now },
    { email: "andy@company.example", displayName: "Andy Lam", region: "HK", role: "Viewer", enabled: true, assignedJobIds: ["QS-2026-01"], createdAt: now, updatedAt: now },
    { email: "carol@company.example", displayName: "Carol Cheung", region: "HK", role: "Recruiter", enabled: true, objectId: "u-carol", tenantId: "demo", assignedJobIds: ["SEC-2026-04", "ACC-2026-05"], createdAt: now, updatedAt: now },
    { email: "david@company.example", displayName: "David Ho", region: "HK", role: "Viewer", enabled: true, objectId: "u-david", tenantId: "demo", assignedJobIds: ["PC-2026-06", "BS-2026-07"], createdAt: now, updatedAt: now },
    { email: "grace@company.example", displayName: "Grace Lee", region: "HK", role: "Viewer", enabled: true, objectId: "u-grace", tenantId: "demo", assignedJobIds: ["DD-2026-02", "ID-2026-08"], createdAt: now, updatedAt: now },
  ],
  updatedAt: now,
  updatedBy: "admin@company.example",
};

function syncDemoAccess(): AccessSettings {
  const timestamp = new Date().toISOString();
  const existingByObjectId = new Map(demoAccess.users.flatMap((entry) => entry.objectId ? [[entry.objectId, entry] as const] : []));
  const existingByEmail = new Map(demoAccess.users.map((entry) => [entry.email, entry] as const));
  demoAccess.users = demoCompanyDirectoryUsers.map((directoryUser) => {
    const existing = existingByObjectId.get(directoryUser.objectId) ?? existingByEmail.get(directoryUser.email);
    return {
      ...directoryUser,
      role: existing?.role ?? "Viewer",
      enabled: existing?.enabled ?? false,
      assignedJobIds: existing?.assignedJobIds ?? [],
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: existing?.updatedAt ?? timestamp,
    };
  });
  demoAccess.directorySyncedAt = timestamp;
  demoAccess.updatedAt = timestamp;
  demoAccess.updatedBy = "company-directory-sync";
  return structuredClone(demoAccess);
}

function sampleJobMarkdown(job: Pick<JobConfig, "jobId" | "jobTitle" | "department" | "location">): string {
  return `# Job Information\n\nJob ID: ${job.jobId}\nJob Title: ${job.jobTitle}\nDepartment: ${job.department}\nLocation: ${job.location}\n\n# Role Summary\n\nLead commercial delivery across complex interior fit-out programmes.\n\n# Scoring Categories\n\n## Experience\n\nWeight: 30\nMax Score: 10\n\n### Requirements\n- Ten years of relevant experience\n- End-to-end commercial ownership\n\n## Technical Skills\n\nWeight: 30\nMax Score: 10\n\n### Requirements\n- Cost planning and tender analysis\n- Contract administration\n\n## Project Fit\n\nWeight: 20\nMax Score: 10\n\n### Requirements\n- Aviation or hospitality fit-out\n\n## Education and Qualifications\n\nWeight: 10\nMax Score: 10\n\n### Requirements\n- Relevant degree or professional qualification\n\n## Communication and Stakeholder Management\n\nWeight: 10\nMax Score: 10\n\n### Requirements\n- Client and subcontractor communication\n\n# Knockout Criteria\n\nNone specified.\n`;
}
